import { packagesAPI, creditsAPI, historyAPI, toolCreditCostsAPI, orderAPI } from './api';

export interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price: number;
  original_price?: number;
  discount_label?: string;
  is_popular: boolean;
  features: string[];
  sort_order: number;
  is_active: boolean;
}

export interface PurchaseOrder {
  id: string;
  user_id: string;
  package_id?: string;
  credits: number;
  amount: number;
  payment_method?: string;
  payment_status: string;
  transaction_id?: string;
  created_at: string;
}

export interface ToolCreditCost {
  id: string;
  tool_id: string;
  tool_name: string;
  credits_per_use: number;
  description?: string;
  is_active: boolean;
}

// 将ISO 8601日期格式转换为MySQL支持的格式
function formatDateForMySQL(date: Date): string {
  return date.toISOString().replace('T', ' ').replace('Z', '');
}

// 获取所有积分套餐
export async function getCreditPackages(): Promise<CreditPackage[]> {
  try {
    const data = await packagesAPI.getCreditPackages();
    return (data || []).map((pkg: any) => {
      let features = pkg.features;
      if (typeof features === 'string') {
        try {
          features = JSON.parse(features);
        } catch {
          features = [];
        }
      }
      return {
        ...pkg,
        price: Number(pkg.price),
        original_price: pkg.original_price != null ? Number(pkg.original_price) : undefined,
        credits: Number(pkg.credits),
        features: Array.isArray(features) ? features : [],
      } as CreditPackage;
    });
  } catch (error) {
    console.error('获取积分套餐失败:', error);
    return [];
  }
}

// 获取工具积分消耗配置
export async function getToolCreditCosts(): Promise<ToolCreditCost[]> {
  try {
    const data = await toolCreditCostsAPI.getToolCreditCosts();
    return data || [];
  } catch (error) {
    console.error('获取工具积分消耗配置失败:', error);
    return [];
  }
}

// 获取指定工具的积分消耗
export async function getToolCreditCost(toolId: string): Promise<ToolCreditCost | null> {
  try {
    const data = await toolCreditCostsAPI.getToolCreditCosts();
    return data.find(cost => cost.tool_id === toolId) || null;
  } catch (error) {
    console.error('获取工具积分消耗失败:', error);
    return null;
  }
}

// 创建订单（状态为pending，支付成功后更新）
export async function createPurchaseOrder(
  userId: string,
  packageId: string,
  credits: number,
  amount: number
): Promise<{ success: boolean; orderId?: string; error?: string }> {
  try {
    const result = await orderAPI.createOrder(userId, packageId, credits, amount);
    return result;
  } catch (error: any) {
    return { success: false, error: error.message || '创建订单失败' };
  }
}

// 完成购买（别名，兼容旧代码）
export async function completePurchase(
  orderId: string,
  transactionId: string
): Promise<{ success: boolean; error?: string }> {
  return completePurchaseOrder(orderId, transactionId);
}

// 更新订单状态为已完成并增加用户积分
export async function completePurchaseOrder(
  orderId: string,
  transactionId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const result = await orderAPI.completeOrder(orderId, transactionId);
    return result;
  } catch (error: any) {
    return { success: false, error: error.message || '完成订单失败' };
  }
}

// 获取用户的订单历史（别名，兼容旧代码）
export async function getUserOrders(userId: string): Promise<PurchaseOrder[]> {
  return getUserPurchaseOrders(userId);
}

// 获取用户的购买订单历史
export async function getUserPurchaseOrders(userId: string): Promise<PurchaseOrder[]> {
  try {
    const data = await orderAPI.getUserOrders(userId);
    return data || [];
  } catch (error) {
    console.error('获取用户订单失败:', error);
    return [];
  }
}

// 取消订单
export async function cancelPurchaseOrder(orderId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const result = await orderAPI.cancelOrder(orderId);
    return result;
  } catch (error: any) {
    return { success: false, error: error.message || '取消订单失败' };
  }
}

// 退款订单（回收积分）
export async function refundPurchaseOrder(orderId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const result = await orderAPI.refundOrder(orderId);
    return { success: !!result.success };
  } catch (error: any) {
    return { success: false, error: error.message || '退款失败' };
  }
}

// 获取订单详情
export async function getOrderById(orderId: string): Promise<PurchaseOrder | null> {
  try {
    const data = await orderAPI.getOrderById(orderId);
    return data || null;
  } catch (error) {
    console.error('获取订单详情失败:', error);
    return null;
  }
}

// 获取用户的所有积分交易记录
export async function getUserCreditTransactions(userId: string): Promise<any[]> {
  try {
    const data = await creditsAPI.getUserCreditTransactions(userId);
    return data || [];
  } catch (error) {
    console.error('获取积分交易记录失败:', error);
    return [];
  }
}

// 扣除用户积分
export async function deductUserCredits(
  userId: string,
  amount: number,
  description: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const result = await creditsAPI.deductCredits(userId, amount, description);
    return { success: result.success };
  } catch (error: any) {
    return { success: false, error: error.message || '扣除积分失败' };
  }
}

// 获取用户当前积分
export async function getUserCredits(userId: string): Promise<number> {
  try {
    const data = await creditsAPI.getUserCredits(userId);
    return data?.credits || 0;
  } catch (error) {
    console.error('获取用户积分失败:', error);
    return 0;
  }
}
